import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { assignedUsersQueryKeys } from './assignedUsersQueryKeys';
import { AssignedUsersToast } from './AssignedUsersToast';
import { invalidateAssignedUsers, isAssignmentConflict } from './assignmentConflict';
import { APICommunicator } from '~/api/APICommunicator';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';

export const useSetActiveUserMutation = (loanRequestId: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const invalidateOnChainComplete = useChainAwareInvalidation();

    return useMutation({
        // the name only feeds the toast
        mutationFn: ({ userId }: { name: string; userId: string }) =>
            APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.setActiveUser,
                payload: { userId },
            }),
        onError: (error: Error & { status?: number }) => {
            if (isAssignmentConflict(error)) {
                toast.error(t('LEADS.ASSIGNED_USERS.CHANGED_MEANWHILE'));
                return;
            }
            Sentry.captureException(error);
            toast.error(t('LEADS.ASSIGNED_USERS.SET_ACTIVE_ERROR'));
        },
        onSettled: (data) => {
            // failed: refetch now, a conflict means it changed meanwhile and other errors (e.g. timeout) may have persisted
            if (!data) {
                invalidateAssignedUsers(queryClient, loanRequestId);
                return;
            }
            invalidateOnChainComplete({ correlationId: data.id, queryKeys: assignedUsersQueryKeys(loanRequestId) });
        },
        onSuccess: (_data, { name }) => {
            toast.success(
                <AssignedUsersToast
                    body={t('LEADS.ASSIGNED_USERS.NOW_RESPONSIBLE', { name })}
                    title={t('LEADS.ASSIGNED_USERS.SET_ACTIVE_SUCCESS')}
                />,
            );
        },
    });
};
